import { useMutation } from '@tanstack/react-query'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { FxButton, FxPanel } from '@/components/fx'
import { templatesApi } from '@/lib/api'
import { DialogShell } from './DialogShell'

/**
 * "Archivieren" — out of the Übersicht, not out of existence.
 *
 * The header's overflow menu carries the same action, as both artboards
 * draw it; this is the one the side-nav points at. It confirms first,
 * because the person leaves the screen on success and would otherwise have
 * no way to notice what they hit.
 */
export function ArchiveSection({
  templateId,
  title,
  canWrite,
}: {
  templateId: string
  title: string
  canWrite: boolean
}) {
  const navigate = useNavigate()
  const [asking, setAsking] = useState(false)

  const archive = useMutation({
    mutationFn: () => templatesApi.archive(templateId),
    onSuccess: () => navigate('/'),
  })

  return (
    <FxPanel title="Archivieren" className="p-5 xs:p-8 sm:p-10">
      <p className="mb-4 mt-0 text-small text-text-muted">
        Das Musterformular verschwindet aus der Übersicht, bleibt aber mit allen Dokumenten
        erhalten und lässt sich im Archiv wiederherstellen.
      </p>
      <FxButton variant="outlined" disabled={!canWrite} onClick={() => setAsking(true)}>
        Musterformular archivieren
      </FxButton>
      {!canWrite && (
        <p className="mb-0 mt-3 text-small text-text-muted">
          Dafür brauchen Sie Schreibrechte an diesem Musterformular.
        </p>
      )}

      <DialogShell open={asking} onClose={() => setAsking(false)} title="Archivieren?">
        <div className="flex flex-col gap-4 text-body text-text">
          <p className="m-0">
            „{title}“ wird aus der Übersicht entfernt. Die Dokumente bleiben erhalten, und im Archiv
            holen Sie das Formular jederzeit zurück.
          </p>
          {archive.isError && (
            <p role="alert" className="m-0 text-small text-danger-text">
              Das Archivieren hat nicht geklappt.
            </p>
          )}
          <div className="flex justify-end gap-3">
            <FxButton variant="outlined" onClick={() => setAsking(false)} disabled={archive.isPending}>
              Abbrechen
            </FxButton>
            <FxButton onClick={() => archive.mutate()} disabled={archive.isPending}>
              {archive.isPending ? 'Wird archiviert …' : 'Archivieren'}
            </FxButton>
          </div>
        </div>
      </DialogShell>
    </FxPanel>
  )
}
